import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { lmsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, CheckCircle2, Circle, PlayCircle, Award, FileText, BookOpen } from 'lucide-react';

function VideoPlayer({ source, url }) {
  if (!url) return <div className="aspect-video bg-[#F1F5F9] rounded-lg flex items-center justify-center text-[#64748B]">No video</div>;
  if (source === 'embed' || /youtu\.?be|vimeo|loom/i.test(url)) {
    let embed = url;
    const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
    if (yt) embed = `https://www.youtube.com/embed/${yt[1]}`;
    return <iframe src={embed} className="w-full aspect-video rounded-lg" allowFullScreen frameBorder="0" data-testid="lesson-video-iframe" />;
  }
  return <video src={url} controls className="w-full max-h-[60vh] rounded-lg bg-black" data-testid="lesson-video-file" />;
}

export default function CoursePlayerPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  const [cert, setCert] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      await lmsApi.enroll(courseId).catch(() => {});
      const { data } = await lmsApi.getCourse(courseId);
      setCourse(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [courseId]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0E7490]" /></div>;
  if (!course) return <div className="p-8 text-center text-[#64748B]">Course not found</div>;

  const lessons = course.lessons || [];
  const active = lessons[activeIdx];
  const progress = course.enrollment?.lesson_progress || {};
  const completedSet = new Set(Object.entries(progress).filter(([_, p]) => p?.completed).map(([id]) => id));
  const doneCount = completedSet.size;
  const completedAll = !!course.enrollment?.completed_at;

  const markComplete = async () => {
    if (!active) return;
    setMarking(true);
    try {
      const { data } = await lmsApi.markLesson(active.id, true);
      if (data.certificate) {
        setCert(data.certificate);
        toast.success(`Certificate issued: ${data.certificate.certificate_number}`);
      } else {
        toast.success('Lesson marked complete');
      }
      await load();
      if (activeIdx < lessons.length - 1) setActiveIdx(activeIdx + 1);
    } catch (err) { toast.error('Could not save progress'); }
    finally { setMarking(false); }
  };

  return (
    <div className="space-y-4" data-testid="course-player-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/learn')} data-testid="back-to-catalog-btn"><ArrowLeft className="w-4 h-4 mr-1" />Back to courses</Button>

      {course.banner_url && <img src={course.banner_url} alt="" className="w-full max-h-48 object-cover rounded-lg" />}

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>{course.title}</h1>
          <p className="text-sm text-[#64748B] mt-1">{course.description}</p>
          <div className="flex items-center gap-2 mt-2">
            <Badge className="bg-[#EFF6FF] text-[#1E40AF]">{lessons.length} lessons</Badge>
            <Badge className="bg-[#F1F5F9] text-[#0A2342]">{doneCount}/{lessons.length} completed</Badge>
            {completedAll && <Badge className="bg-[#FEF3C7] text-[#92400E]"><Award className="w-3 h-3 mr-1" />Certified</Badge>}
          </div>
        </div>
        {course.enrollment?.certificate_number && (
          <Card className="careiq-card border-l-4 border-l-[#CA8A04]">
            <CardContent className="p-3 text-xs">
              <div className="font-semibold text-[#92400E] flex items-center gap-1"><Award className="w-3.5 h-3.5" />Certificate</div>
              <div className="text-[#92400E] font-mono">{course.enrollment.certificate_number}</div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-3" data-testid="lesson-content">
          {!active && <Card className="careiq-card"><CardContent className="p-10 text-center text-[#64748B]">No lessons yet.</CardContent></Card>}
          {active && (
            <Card className="careiq-card">
              <CardContent className="p-4 space-y-3">
                <h2 className="text-lg font-semibold text-[#0A2342]">Lesson {activeIdx + 1}: {active.title}</h2>
                {active.lesson_type === 'video' && <VideoPlayer source={active.video_source} url={active.video_url} />}
                {active.lesson_type === 'article' && (
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: active.content_html || '' }} data-testid="lesson-article-content" />
                )}
                {active.lesson_type === 'quiz' && (
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: active.content_html || '<p><em>Quiz coming soon — for now, mark complete after reviewing materials below.</em></p>' }} />
                )}
                <div className="flex justify-between items-center pt-2 border-t border-[#E2E8F0]">
                  <Button variant="outline" disabled={activeIdx === 0} onClick={() => setActiveIdx(activeIdx - 1)} data-testid="prev-lesson-btn">Previous</Button>
                  <Button className="careiq-btn-primary" onClick={markComplete} disabled={marking} data-testid="mark-complete-btn">
                    {marking && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}
                    {completedSet.has(active.id) ? 'Mark complete again' : 'Mark complete'}
                    {activeIdx < lessons.length - 1 && ' & next →'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <Card className="careiq-card h-fit" data-testid="lesson-sidebar">
          <CardContent className="p-3 space-y-1">
            <div className="text-xs font-bold uppercase tracking-wider text-[#64748B] mb-2">Curriculum</div>
            {lessons.map((l, i) => (
              <button key={l.id} onClick={() => setActiveIdx(i)} className={`w-full text-left p-2 rounded-lg flex items-start gap-2 ${i === activeIdx ? 'bg-[#0E7490]/10 border border-[#0E7490]/30' : 'hover:bg-[#F8FAFC]'}`} data-testid={`lesson-item-${i}`}>
                {completedSet.has(l.id) ? <CheckCircle2 className="w-4 h-4 text-[#16A34A] flex-shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-[#94A3B8] flex-shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-[#64748B]">Lesson {i + 1} · {l.lesson_type}</div>
                  <div className="text-sm text-[#0A2342] font-medium truncate">{l.title}</div>
                </div>
                {l.lesson_type === 'video' && <PlayCircle className="w-4 h-4 text-[#94A3B8] flex-shrink-0 mt-0.5" />}
                {l.lesson_type === 'article' && <FileText className="w-4 h-4 text-[#94A3B8] flex-shrink-0 mt-0.5" />}
                {l.lesson_type === 'quiz' && <BookOpen className="w-4 h-4 text-[#94A3B8] flex-shrink-0 mt-0.5" />}
              </button>
            ))}
          </CardContent>
        </Card>
      </div>

      {cert && (
        <Card className="careiq-card border-2 border-[#CA8A04] bg-gradient-to-br from-[#FEF3C7] to-white">
          <CardContent className="p-6 text-center" data-testid="cert-banner">
            <Award className="w-12 h-12 text-[#CA8A04] mx-auto" />
            <h3 className="text-xl font-bold text-[#92400E] mt-2">Congratulations!</h3>
            <p className="text-sm text-[#92400E]">You've earned certificate <span className="font-mono font-bold">{cert.certificate_number}</span> for completing <em>{cert.course_title}</em>.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
